import {useEffect, useState} from 'react';
import { TrashIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';

import './App.css';
import './index.css';
import Titlebar from './Titlebar';
import { apiUrl } from './config';

function CustomerTable() {    
  const navigate = useNavigate();
  const [ customers, setCustomers ] = useState(null);
  const [ checkedItems, setCheckedItems ] = useState([]);
  const [ pagination, setPagination ] = useState({});
  const [ isLoading, setIsLoading ] = useState(false);

  const fetchCustomers = async (page = 1, limit = 10) => {
    setIsLoading(true);
    try {
      const response = await fetch(apiUrl(`/customer/all?page=${page}&page_size=${limit}`));
      const customer_data = await response.json();

      // Process customer data with order counts and spending
      if (customer_data.data && Array.isArray(customer_data.data)) {
        for (let i = 0; i < customer_data.data.length; i++) {
          if (!customer_data.data[i].monthly_spend) {
            try {
              const orders = await fetch(apiUrl(`/order/customer/${customer_data.data[i]._id}`));
              const order_data = await orders.json();
              
              // Guard: Check if order_data exists and is an array
              if (order_data && Array.isArray(order_data) && order_data.length > 0) {
                let total = 0;
                for (let j = 0; j < order_data.length; j++) {
                  total += order_data[j].total_cost || 0; // Also guard against missing total_cost
                }
                customer_data.data[i].monthly_spend = total;
                customer_data.data[i].order_count = order_data.length;
              } else {
                // No orders found - set defaults
                customer_data.data[i].monthly_spend = 0;
                customer_data.data[i].order_count = 0;
              }
            } catch (error) {
              console.error(`Error fetching orders for customer ${customer_data.data[i]._id}:`, error);
              customer_data.data[i].monthly_spend = 0;
              customer_data.data[i].order_count = 0;
            }
          }
          
          // Final fallback - ensure monthly_spend is never null/undefined
          if (!customer_data.data[i].monthly_spend) {
            customer_data.data[i].monthly_spend = 0;
          }
          
          // Ensure order_count is never null/undefined
          if (!customer_data.data[i].order_count) {
            customer_data.data[i].order_count = 0;
          }
        }
      }

      setCustomers(customer_data.data || []);
      setPagination(customer_data.pagination);
      
      console.log('Customer data:', customer_data);
    } catch (error) {
      console.error('Error fetching customers:', error);
      setCustomers([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {  
    fetchCustomers(pagination.current_page, pagination.page_size);
  }, []);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.total_pages && newPage !== pagination.current_page) {
      fetchCustomers(newPage, pagination.page_size);
      setCheckedItems([]); // Clear selections when changing pages
    }
  };

  const handleItemsPerPageChange = (newLimit) => {
    fetchCustomers(1, newLimit); // Reset to page 1 when changing items per page
    setCheckedItems([]);
  };

  if (customers === null || isLoading) {
    return (
      <div className="lg:pl-72 sm:ml-0 px-0 sm:px-0">
        <Titlebar title="Customers"/>
        <div className="m-3 px-4 sm:px-6 lg:px-8">
          <div className="text-center py-12">
            <div className="text-gray-500">Loading customers...</div>
          </div>
        </div>
      </div>
    );
  }

  const handleClick = () => {
    console.log('Add Customer');
    navigate('/customeradd');
  }

  const handleCheckboxChange = (customerId) => {
    setCheckedItems(prev => {
      if (prev.includes(customerId)) {
        return prev.filter(id => id !== customerId);
      } else {
        return [...prev, customerId];
      }
    });
  };

  const deleteCustomers = async () => {
    if (checkedItems.length === 0) return;
    
    if (window.confirm(`Are you sure you want to delete ${checkedItems.length} customer(s)?`)) {
      try {
        // Delete each selected customer
        await Promise.all(
          checkedItems.map(customerId => 
            fetch(apiUrl(`/customer/delete/${customerId}`), {
              method: 'DELETE'
            })
          )
        );
        
        // Refresh the current page
        fetchCustomers(pagination.current_page, pagination.page_size);
        setCheckedItems([]);
        
        console.log('Customers deleted successfully');
      } catch (error) {
        console.error('Error deleting customers:', error);
      }
    }
  };

  const handleViewCustomer = (customerId) => {
    console.log('View customer:', customerId);
    navigate(`/customerview/${customerId}`);
  };

  const handleEditCustomer = (customerId) => {
    console.log('Edit customer:', customerId);
    navigate(`/customeredit/${customerId}`);
  };

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    const { current_page, total_pages } = pagination;

    let startPage = Math.max(1, current_page - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(total_pages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    
    return pages;
  };

  return (
    <div className="lg:pl-72 sm:ml-0 px-0 sm:px-0">
    <Titlebar title="Customers"/>

    <div className="m-3 px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <p className="mt-2 text-sm text-gray-700">
            Showing {customers.length} of {pagination.total_count} customers

          </p>
        </div>
      </div>
      
      <div className="mt-8 flow-root">
        <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
            {customers && Array.isArray(customers) ? (
              <table className="min-w-full divide-y divide-gray-300">
                <thead>
                  <tr>
                    <th className="w-1.5 px-3 py-3 text-sm font-semibold text-gray-900 sm:table-cell">
                      <button
                        type="button"
                        onClick={deleteCustomers}
                        disabled={!checkedItems.length > 0}
                        className="rounded-full bg-indigo-600 p-1 text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50"
                      >
                        <TrashIcon aria-hidden="true" className="h-5 w-5" />
                      </button>
                    </th>
                    <th scope="col" className="py-1.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-0">
                      Company Name
                    </th>
                    <th scope="col" className="px-3 py-1.5 text-left text-sm font-semibold text-gray-900">
                      Contact
                    </th>
                    <th scope="col" className="px-3 py-1.5 text-left text-sm font-semibold text-gray-900">
                      Address
                    </th>
                    <th scope="col" className="px-3 py-1.5 text-left text-sm font-semibold text-gray-900">
                      Active Orders
                    </th>
                    <th scope="col" className="px-3 py-1.5 text-left text-sm font-semibold text-gray-900">
                      Current Spend
                    </th>
                    <th scope="col" className="relative py-1.5 pl-3 pr-4 sm:pr-0">
                      <button
                        type="button"
                        onClick={handleClick}
                        className="block rounded-md bg-indigo-600 px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                      >
                        Add Customer
                      </button>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {customers.map((customer) => (
                    <tr key={customer._id}>
                      <td className="w-1.5 px-3 py-5 text-sm font-medium text-gray-900 sm:table-cell">
                        <input
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                          type="checkbox"
                          checked={checkedItems.includes(customer._id)}
                          onChange={() => handleCheckboxChange(customer._id)}
                        />
                      </td>
                      <td className="whitespace-nowrap py-5 pl-4 pr-3 text-sm sm:pl-0">
                        <div className="flex items-center">
                          <div className="ml-4">
                            <div className="font-medium text-gray-900">{customer.name}</div>
                            <div className="mt-1 text-gray-500">{customer.telephone}</div>
                          </div>
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-3 py-5 text-sm text-gray-500">
                        <div className="text-gray-900">{customer.contact_name}</div>
                        <div className="mt-1 text-gray-500">{customer.contact_email}</div>
                      </td>
                      <td className="whitespace-nowrap px-3 py-5 text-sm text-gray-500">
                        <div className="text-gray-900">{customer.address_1}</div>
                        <div className="mt-1 text-gray-500">{customer.address_2}</div>
                        <div className="mt-1 text-gray-500">{customer.city}, {customer.state} {customer.zip_code}</div>
                      </td>
                      <td className="whitespace-nowrap px-3 py-5 text-sm text-gray-500">
                        <span className="inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20">
                          {customer.order_count}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-3 py-5 text-sm text-gray-500">${customer.monthly_spend}</td>
                      <td className="relative whitespace-nowrap py-5 pl-3 pr-4 text-right text-sm font-medium sm:pr-0">
                        <button 
                          onClick={() => handleViewCustomer(customer._id)}
                          className="text-indigo-600 hover:text-indigo-900 mr-2"
                        >
                          View<span className="sr-only">, Customer {customer._id}</span>
                        </button>
                        &nbsp; | &nbsp; 
                        <button 
                          onClick={() => handleEditCustomer(customer._id)}
                          className="text-indigo-600 hover:text-indigo-900"
                        >
                          Edit<span className="sr-only">, Customer {customer._id}</span>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="text-center py-12">
                <div className="text-gray-500">
                  {customers === null ? 'No customers data available.' : 'Unable to load customers.'}
                </div>
              </div>
            )}
            
            {customers && Array.isArray(customers) && customers.length === 0 && (
              <div className="text-center py-12">
                <div className="text-gray-500">No customers found.</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Pagination Controls - Always visible for testing */}
      {(pagination.totalPages > 1 || true) && (
        <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
          <div className="flex flex-1 justify-between sm:hidden">
            <button
              onClick={() => handlePageChange(pagination.current_page - 1)}
              disabled={pagination.current_page <= 1}
              className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => handlePageChange(pagination.current_page + 1)}
              disabled={pagination.current_page >= pagination.total_pages}
              className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
            <div className="flex items-center space-x-2">
              <p className="text-sm text-gray-700">
                Page <span className="font-medium">{pagination.current_page}</span> of{' '}
                <span className="font-medium">{pagination.total_pages}</span>
                <span className="text-xs text-gray-500 ml-2">
                  (Total: {pagination.total_count} customers)
                </span>
              </p>
              <select
                value={pagination.page_size}
                onChange={(e) => handleItemsPerPageChange(parseInt(e.target.value))}
                className="rounded-md border-gray-300 text-sm focus:border-indigo-500 focus:ring-indigo-500"
              >
                <option value={5}>5 per page</option>
                <option value={10}>10 per page</option>
                <option value={25}>25 per page</option>
                <option value={50}>50 per page</option>
              </select>
            </div>
            <div>
              <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                <button
                  onClick={() => handlePageChange(pagination.current_page - 1)}
                  disabled={pagination.current_page <= 1}
                  className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">Previous</span>
                  <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
                </button>
                
                {getPageNumbers().map((pageNum) => (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                      pageNum === pagination.current_page
                        ? 'z-10 bg-indigo-600 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600'
                        : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0'
                    }`}
                  >
                    {pageNum}
                  </button>
                ))}
                
                <button
                  onClick={() => handlePageChange(pagination.current_page + 1)}
                  disabled={pagination.current_page >= pagination.total_pages}
                  className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">Next</span>
                  <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}
    </div>
    </div>
  )
};

export default CustomerTable;